import React from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Label
} from "recharts";
import styles from "./ProgramDistributionChart.module.css";
import Skeleton from "../Skeleton";

const ProgramDistributionChart = ({
  data = [],
  title = "Program Distribution",
  colors = ["#1b254b", "#3b82f6", "#16a249"],
  isLoading = false
}) => {
  // Total for percentage calculation 
  const total = data[0]?.total ?? data.reduce((sum, item) => sum + item.value, 0);
  const dataWithTotal = data.map((item) => ({ ...item, total }));

  // Filter out zero-value slices 
  const filteredData = dataWithTotal
    .map((item, index) => ({ ...item, originalIndex: index }))
    .filter(item => item.value > 0);

  // No padding (gap) when there is only one visible slice 
  const paddingAngle = filteredData.length > 1 ? 2 : 0;

  const getPercent = (item) => {
    if (typeof item?.pct === "number") return item.pct;
    if (!item?.total) return 0;
    return (item.value / item.total) * 100;
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const percentage = getPercent(data.payload).toFixed(2);

      return (
        <div className={styles.tooltip}>
          <p className={styles.tooltipLabel}>{data.name}</p>
          <p
            className={styles.tooltipValue}
            style={{ color: data.color }}
          >
            {data.value} ({percentage}%)
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom legend
  const CustomLegend = ({ data, colors, total }) => {
    return (
      <div className={styles.legend}>
        <div className={`${styles.legendItem} ${styles.legendTotal}`}>
          <span className={styles.legendLabel}>Total</span>
          <span className={styles.legendValue}>{total}</span>
        </div>
        {data.map((entry, index) => (
          <div key={index} className={styles.legendItem}>
            <div
              className={styles.legendDot}
              style={{ backgroundColor: colors[index % colors.length] }}
            />
            <span className={styles.legendLabel}>{entry.name}</span>
            <span className={styles.legendValue}>{entry.value}</span>
          </div>
        ))}
      </div>
    );
  };

  // Custom label for slices
  const renderSliceLabel = ({
    value,
    cx,
    cy,
    midAngle,
    outerRadius,
    payload,
    index
  }) => {
    if (!total || value <= 0) return null;

    const percentRaw = getPercent(payload);
    if (percentRaw <= 0) return null;

    const percent = Math.round(percentRaw);
    if (percent === 0) return null;

    const RADIAN = Math.PI / 180;
    const lineStartRadius = outerRadius;
    const labelRadius = outerRadius + 16;

    const startX = cx + lineStartRadius * Math.cos(-midAngle * RADIAN);
    const startY = cy + lineStartRadius * Math.sin(-midAngle * RADIAN);
    const endX = cx + labelRadius * Math.cos(-midAngle * RADIAN);
    const endY = cy + labelRadius * Math.sin(-midAngle * RADIAN);
    const textAnchor = endX > cx ? "start" : "end";
    const color = colors[payload.originalIndex % colors.length] || "#111827";

    return (
      <>
        <line
          x1={startX}
          y1={startY}
          x2={endX}
          y2={endY}
          stroke={color}
          strokeWidth={1}
        />
        <text
          x={endX}
          y={endY}
          fill={color}
          textAnchor={textAnchor}
          dominantBaseline="central"
          fontSize={12}
          fontWeight={600}
        >
          {percent}%
        </text>
      </>
    );
  };

  return (
    <div className={styles.chartContainer}>
      {title && <h3 className={styles.chartTitle}>{title}</h3>}

      {isLoading ? (
        <div className={styles.chartContent}>
          <div className={styles.chartArea}>
            <div className={styles.emptyState}>
              <div className={`${styles.emptyRing} ${styles.loadingRing}`}>
                <span className={styles.emptyCenterValue}>...</span>
              </div>
              <div className={styles.emptyText}>Loading...</div>
            </div>
          </div>
          <div className={styles.legend}>
            <div className={`${styles.legendItem} ${styles.legendTotal}`}>
              <Skeleton width="60%" height="8px" radius="999px" />
            </div>
            <div className={styles.legendItem}>
              <Skeleton width="75%" height="8px" radius="999px" />
            </div>
            <div className={styles.legendItem}>
              <Skeleton width="55%" height="8px" radius="999px" />
            </div>
            <div className={styles.legendItem}>
              <Skeleton width="70%" height="8px" radius="999px" />
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.chartContent}>
          <div className={styles.chartArea} style={{ overflow: "visible" }}>
            {total <= 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyRing}>
                  <span className={styles.emptyCenterValue}>0</span>
                </div>
                <div className={styles.emptyText}>No data available</div>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={400} style={{ overflow: "visible" }}>
                <PieChart margin={{ top: 20, right: 110, bottom: 20, left: 110 }}>
                  <Pie
                    data={filteredData} // filtered for gaps
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={94}
                    paddingAngle={paddingAngle}
                    dataKey="value"
                    label={renderSliceLabel}
                    labelLine={false}
                  >
                    {filteredData.map((entry) => (
                      <Cell
                        key={`cell-${entry.name}`}
                        fill={colors[entry.originalIndex % colors.length]}
                      />
                    ))}
                    <Label value={total} position="center" className={styles.centerLabel} />
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
          <CustomLegend data={dataWithTotal} colors={colors} total={total} />
        </div>
      )}
    </div>
  );
};

export default ProgramDistributionChart;
